import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { KnowledgeBaseCategory } from '@hefe/models';
import {
  KnowledgeBaseApi,
  KnowledgeBaseSlice,
  currentUserIsAdminSelector,
} from '@hefe/store';
import {
  Button,
  Col,
  Divider,
  Input,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Tree,
  Typography,
} from 'antd';
import { DataNode } from 'antd/es/tree';
import { Key, PropsWithChildren, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store/store';

export default function KnowledgeBaseLayout(props: PropsWithChildren<any>) {
  const userIsAdmin = useSelector(currentUserIsAdminSelector);
  const expandedKeys = useSelector(KnowledgeBaseSlice.expandedKeysSelector);
  const selectedKey = useSelector(KnowledgeBaseSlice.selectedKeySelector);
  const navigate = useNavigate();
  const { id } = useParams();

  const dispatch = useAppDispatch();

  const selectedCategoryId = parseInt(id ?? '0', 10);

  const { data: navigation = [], isFetching: isFetchingNavigation } =
    KnowledgeBaseApi.useFetchNavigationQuery();

  const [addCategory, { isLoading: isSavingCategory }] =
    KnowledgeBaseApi.useAddCategoryMutation();

  const [updateCategory, { isLoading: isUpdatingCategory }] =
    KnowledgeBaseApi.useUpdateCategoryMutation();

  const [updateNavigation, { isLoading: isUpdatingNavigation }] =
    KnowledgeBaseApi.useUpdateCategoryNavigationMutation();

  const [deleteCategory, { isLoading: isDeletingCategory }] =
    KnowledgeBaseApi.useDeleteCategoryMutation();

  const [newCategoryName, setNewCategoryName] = useState('');

  const onAddCategoryConfirm = async () => {
    await addCategory({ name: newCategoryName });
    setNewCategoryName('');
  };

  const onRenameCategoryConfirm = async () => {
    await updateCategory({
      id: selectedCategoryId,
      request: {
        name: newCategoryName,
      },
    });
    setNewCategoryName('');
  };

  const onSelectTreeKeys = (keys: Key[]) => {
    if (keys && keys.length === 1) {
      const key = keys[0] as string;
      dispatch(KnowledgeBaseSlice.actions.setSelectedKey(key));
      if (key === 'uncategorized') {
        navigate('/knowledge-base/categories/0');
      } else {
        navigate(`/knowledge-base/categories/${key}`);
      }
    }
  };

  const onDropCategory = (
    categoryId: number,
    destinationId: number | undefined,
    asChild: boolean,
    position: number
  ) => {
    if (isNaN(categoryId)) {
      return;
    }

    updateNavigation({
      category_id: categoryId,
      destination_category_id: destinationId,
      as_child: asChild,
      position: position,
    })
      .unwrap()
      .catch((_) => undefined);
  };

  const onDeleteCategory = () => {
    if (isNaN(selectedCategoryId)) {
      return;
    }

    deleteCategory(selectedCategoryId)
      .unwrap()
      .then(() => {
        // Go to uncategorized
        navigate('/knowledge-base/categories/0');
      })
      .catch((_) => undefined);
  };

  const treeData: DataNode[] = useMemo(() => {
    const navigationNode = (category: KnowledgeBaseCategory): DataNode => {
      return {
        title: category.name,
        key: category.id,
        children: category?.children?.map((child) => navigationNode(child)),
      } as DataNode;
    };

    const returnData = navigation.map((category) => navigationNode(category));

    return [
      ...returnData,
      {
        title: t('knowledge_base.categories.uncategorized'),
        key: 'uncategorized',
        checkable: false,
      },
    ];
  }, [navigation]);

  return (
    <Row>
      <Col xs={24} sm={8} lg={6}>
        <Button
          icon={<SearchOutlined />}
          type="text"
          onClick={() => navigate('/knowledge-base')}
        >
          {t('knowledge_base.search.title')}
        </Button>
        <Divider />
        <Typography.Title level={5}>
          {t('knowledge_base.menu')}
        </Typography.Title>
        <Skeleton
          active
          loading={
            isFetchingNavigation || isUpdatingNavigation || isDeletingCategory
          }
          paragraph={{ rows: 8 }}
          title={false}
          style={{ paddingRight: 20 }}
        >
          <Tree
            treeData={treeData}
            draggable={userIsAdmin}
            onSelect={(keys) => onSelectTreeKeys(keys)}
            autoExpandParent={true}
            expandedKeys={expandedKeys}
            onExpand={(keys) => {
              dispatch(KnowledgeBaseSlice.actions.setExpandedKeys(keys));
            }}
            selectedKeys={
              selectedKey ? [selectedKey] : id === '0' ? ['uncategorized'] : []
            }
            onDrop={(info) => {
              onDropCategory(
                parseInt(info.dragNode.key + ''),
                parseInt(info.node.key + ''),
                !info.dropToGap,
                info.dropPosition
              );
            }}
          />
        </Skeleton>
        {!isFetchingNavigation &&
          !isUpdatingNavigation &&
          !isDeletingCategory &&
          userIsAdmin && (
            <Space direction="vertical" size={0}>
              <Divider />
              {userIsAdmin &&
                !isNaN(selectedCategoryId) &&
                selectedCategoryId > 0 && (
                  <Popconfirm
                    title={t('knowledge_base.categories.delete.title')}
                    description={t(
                      'knowledge_base.categories.delete.description'
                    )}
                    onConfirm={onDeleteCategory}
                    overlayStyle={{ maxWidth: 300 }}
                    icon={null}
                  >
                    <Button
                      loading={isSavingCategory}
                      type="text"
                      icon={<DeleteOutlined />}
                    >
                      {t('knowledge_base.categories.delete.title')}
                    </Button>
                  </Popconfirm>
                )}

              {userIsAdmin &&
                !isNaN(selectedCategoryId) &&
                selectedCategoryId > 0 && (
                  <Popconfirm
                    title={
                      <div>
                        <Input
                          onChange={(e) => setNewCategoryName(e.target.value)}
                          value={newCategoryName}
                          placeholder={t('knowledge_base.categories.singular')}
                        />
                      </div>
                    }
                    onConfirm={onRenameCategoryConfirm}
                    okButtonProps={{ disabled: newCategoryName.length < 3 }}
                    icon={null}
                  >
                    <Button
                      loading={isSavingCategory}
                      type="text"
                      icon={<EditOutlined />}
                    >
                      {t('knowledge_base.categories.rename')}
                    </Button>
                  </Popconfirm>
                )}

              <Popconfirm
                title={
                  <div>
                    <Input
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      value={newCategoryName}
                      placeholder={t('knowledge_base.categories.singular')}
                    />
                  </div>
                }
                onConfirm={onAddCategoryConfirm}
                okButtonProps={{ disabled: newCategoryName.length < 3 }}
                icon={null}
              >
                <Button
                  loading={isSavingCategory}
                  type="text"
                  icon={<PlusCircleOutlined />}
                >
                  {t('knowledge_base.categories.add')}
                </Button>
              </Popconfirm>
            </Space>
          )}
        {/*
            <Divider />
        <Typography.Title level={5}>
          {t('knowledge_base.tags.plural')}
        </Typography.Title> 
            */}
      </Col>
      <Col xs={24} sm={16} lg={18}>
        {props.children}
      </Col>
    </Row>
  );
}
